import React, { useState } from 'react'
import {
    Backdrop,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { BillItem, BillItemDamage } from '../../types/bills'
import { getCurrency, getShortFormDate } from '../../helpers/stringHelpers'

interface BillDamageDetailDialogProps {
    open: boolean
    item: BillItem | null
    onClose: () => void
}

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            py: 0.75,
        }}
    >
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            {label}
        </Typography>
        <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, textAlign: 'right' }}
        >
            {value}
        </Typography>
    </Box>
)

const areaDisplay = (damage: BillItemDamage) => {
    if (damage.area_name && damage.area_label) {
        return `${damage.area_name} - ${damage.area_label}`
    }
    return damage.area_name || damage.area_label || 'Not specified'
}

export const BillDamageDetailDialog = ({
    open,
    item,
    onClose,
}: BillDamageDetailDialogProps) => {
    const [fullScreenImage, setFullScreenImage] = useState<string | null>(null)
    const damage = item?.damage

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        bgcolor: '#2a2f35',
                        backgroundImage: 'none',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 2,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        color: 'rgba(255,255,255,0.9)',
                        pr: 6,
                        fontWeight: 600,
                    }}
                >
                    {damage?.description || item?.description || 'Damage details'}
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: 'rgba(255,255,255,0.6)',
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {damage ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <DetailRow label="Area" value={areaDisplay(damage)} />
                            <DetailRow
                                label="Quantity"
                                value={damage.quantity ?? 1}
                            />
                            <DetailRow
                                label="Unit cost"
                                value={getCurrency(damage.unit_cost)}
                            />
                            <DetailRow
                                label="Amount billed"
                                value={getCurrency(item?.amount)}
                            />
                            {damage.created_date && (
                                <DetailRow
                                    label="Recorded"
                                    value={getShortFormDate(damage.created_date)}
                                />
                            )}

                            {damage.attachments?.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}
                                    >
                                        Photos
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {damage.attachments.map((attachment) => (
                                            <Box
                                                key={attachment.id}
                                                component="img"
                                                src={attachment.file}
                                                alt="Damage photo"
                                                onClick={() => setFullScreenImage(attachment.file)}
                                                sx={{
                                                    width: 72,
                                                    height: 72,
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    cursor: 'pointer',
                                                    border: '1px solid rgba(255,255,255,0.1)',
                                                    '&:hover': {
                                                        borderColor: 'rgba(255,255,255,0.35)',
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            No additional damage details are available for this charge.
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            {fullScreenImage && (
                <Backdrop
                    sx={{
                        color: '#fff',
                        zIndex: (theme) => theme.zIndex.modal + 1,
                        flexDirection: 'column',
                    }}
                    open={!!fullScreenImage}
                    onClick={() => setFullScreenImage(null)}
                >
                    <img
                        src={fullScreenImage}
                        style={{
                            maxHeight: '80%',
                            maxWidth: '100%',
                            objectFit: 'contain',
                        }}
                        alt="Full screen damage attachment"
                    />
                    <Button
                        color="error"
                        sx={{ mt: 2, width: '80%', maxWidth: 360 }}
                        variant="contained"
                        onClick={() => setFullScreenImage(null)}
                    >
                        Close
                    </Button>
                </Backdrop>
            )}
        </>
    )
}
